import React, { useState, useEffect, useRef } from 'react';
import TasksPanel from './dashboard/TasksPanel';
import MemoriesPanel from './dashboard/MemoriesPanel';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Dashboard({ refreshKey, onDataChange }) {
  const [section, setSection] = useState('tasks');
  const [stats, setStats] = useState({ tasks: 0, memories: 0, completed: 0 });
  const [booted, setBooted] = useState(false);
  const [bootPhase, setBootPhase] = useState(0);

  // Boot sequence animation
  useEffect(() => {
    const timers = [
      setTimeout(() => setBootPhase(1), 200),   // line 1
      setTimeout(() => setBootPhase(2), 600),   // line 2
      setTimeout(() => setBootPhase(3), 1000),  // line 3
      setTimeout(() => setBootPhase(4), 1400),  // stats
      setTimeout(() => setBooted(true), 1800),  // reveal dashboard
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const statsAbortRef = useRef(null);
  useEffect(() => {
    if (statsAbortRef.current) statsAbortRef.current.abort();
    const controller = new AbortController();
    statsAbortRef.current = controller;
    const signal = controller.signal;
    Promise.allSettled([
      fetch(`${API}/dashboard/tasks`, { signal }).then(r => r.json()),
      fetch(`${API}/dashboard/memories`, { signal }).then(r => r.json()),
      fetch(`${API}/dashboard/tasks?status=completed`, { signal }).then(r => r.json()),
    ]).then(([t, m, c]) => {
      if (!signal.aborted) setStats({
        tasks: t.status === 'fulfilled' ? t.value.data?.length || 0 : 0,
        memories: m.status === 'fulfilled' ? m.value.data?.length || 0 : 0,
        completed: c.status === 'fulfilled' ? c.value.data?.length || 0 : 0,
      });
    });
    return () => controller.abort();
  }, [refreshKey]); // re-fetch when data changes

  // Boot sequence screen
  if (!booted) {
    return (
      <div className="dash-boot">
        <div className="dash-boot-reactor">
          <div className="dash-boot-core" />
          <svg className="dash-boot-ring" viewBox="0 0 80 80"><circle cx="40" cy="40" r="30" /></svg>
          <svg className="dash-boot-ring2" viewBox="0 0 80 80"><circle cx="40" cy="40" r="24" /></svg>
        </div>
        <div className="dash-boot-lines">
          {bootPhase >= 1 && <div className="dash-boot-line">INITIALIZING AVENGERS COMMAND SYSTEM...</div>}
          {bootPhase >= 2 && <div className="dash-boot-line">CONNECTING TO MASHRUR NETWORK... <span className="dash-boot-ok">OK</span></div>}
          {bootPhase >= 3 && <div className="dash-boot-line">LOADING MISSION DATABASE... <span className="dash-boot-ok">OK</span></div>}
          {bootPhase >= 4 && (
            <div className="dash-boot-line dash-boot-line-stats">
              <span>{stats.tasks} ACTIVE</span>
              <span className="dash-boot-sep">|</span>
              <span>{stats.memories} INTEL</span>
              <span className="dash-boot-sep">|</span>
              <span>{stats.completed} COMPLETE</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* Header with stats */}
      <div className="dash-header">
        <div className="dash-header-top">
          <div className="dash-header-title-area">
            <div className="dash-header-bar" />
            <h1 className="dash-header-title">OPERATIONS</h1>
          </div>
          <div className="dash-stats">
            {[
              { v: stats.tasks, l: 'ACTIVE', c: '#7b68ee' },
              { v: stats.memories, l: 'INTEL', c: '#e429f2' },
              { v: stats.completed, l: 'DONE', c: '#22c55e' },
            ].map(s => (
              <div key={s.l} className="dash-stat">
                <span className="dash-stat-num" style={{ color: s.c }}>{s.v}</span>
                <span className="dash-stat-label">{s.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Section selector */}
        <div className="dash-sections" role="tablist" aria-label="Dashboard sections">
          {[
            { k: 'tasks', icon: '◇', label: 'MISSIONS', desc: 'Tasks & Schedule' },
            { k: 'memories', icon: '◈', label: 'DATABASE', desc: 'Intelligence Files' },
          ].map(s => (
            <button key={s.k} role="tab" id={`tab-${s.k}`} aria-selected={section === s.k} aria-controls="dash-tabpanel" className={`dash-section-btn ${section === s.k ? 'dash-section-active' : ''}`} onClick={() => setSection(s.k)}>
              <span className="dash-section-icon">{s.icon}</span>
              <div className="dash-section-text">
                <span className="dash-section-label">{s.label}</span>
                <span className="dash-section-desc">{s.desc}</span>
              </div>
              {/* Bottom glow handled by .dash-section-active::after */}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="dash-content" role="tabpanel" id="dash-tabpanel" aria-labelledby={`tab-${section}`}>
        {section === 'tasks' ? <TasksPanel api={API} refreshKey={refreshKey} onDataChange={onDataChange} /> : <MemoriesPanel api={API} refreshKey={refreshKey} onDataChange={onDataChange} />}
      </div>
    </div>
  );
}

export default React.memo(Dashboard);
